import { useState, useEffect, useMemo } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { motion } from 'framer-motion'
import projectService from '../services/projectService'
import { ArrowLeft, Eye, Share2, Calendar, Target, User, MapPin, Mail, Globe, AlertCircle, Edit, HelpCircle } from 'lucide-react'
import { getCurrencySymbol } from '../constants/index.js'
import { Button } from '../components/ui/Button'
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/Card'
import { Badge } from '../components/ui/Badge'
import { Skeleton } from '../components/ui/Skeleton'
import { PROJECT_STATUS } from '../constants/index.js'
import { useAuth } from '../hooks/useAuth'
import ShareModal from '../components/ui/ShareModal'

const ProjectDetailPage = () => {
  const { slug } = useParams()
  const navigate = useNavigate()
  const { user } = useAuth()
  const [project, setProject] = useState(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [resubmitting, setResubmitting] = useState(false)
  const [isShareModalOpen, setIsShareModalOpen] = useState(false)

  useEffect(() => {
    const fetchProject = async () => {
      try {
        setLoading(true)
        setError(null)
        const response = await projectService.getProjectBySlug(slug)
        setProject(response.data)
      } catch (err) {
        console.error('Failed to fetch project:', err)
        setError(err.message || 'Failed to load project')
      } finally {
        setLoading(false)
      }
    }

    if (slug) fetchProject()
  }, [slug])

  const statusConfig = useMemo(() => {
    const configs = {
      [PROJECT_STATUS.DRAFT]: { color: 'bg-slate-100 text-slate-800 border-slate-200', text: 'Draft' },
      [PROJECT_STATUS.SUBMITTED]: { color: 'bg-blue-50 text-blue-700 border-blue-200', text: 'Under Review' },
      [PROJECT_STATUS.APPROVED]: { color: 'bg-emerald-50 text-emerald-700 border-emerald-200', text: 'Approved' },
      [PROJECT_STATUS.REJECTED]: { color: 'bg-red-50 text-red-700 border-red-200', text: 'Rejected' },
      [PROJECT_STATUS.ACTIVE]: { color: 'bg-primary-teal/10 text-primary-teal border-primary-teal/30', text: 'Active' },
      [PROJECT_STATUS.COMPLETED]: { color: 'bg-purple-50 text-purple-700 border-purple-200', text: 'Completed' },
      [PROJECT_STATUS.CANCELLED]: { color: 'bg-orange-50 text-orange-700 border-orange-200', text: 'Cancelled' }
    }
    return configs[project?.status] || { color: 'bg-slate-100 text-slate-800 border-slate-200', text: project?.status }
  }, [project?.status])

  const fundingPercentage = useMemo(() => 
    project?.funding_goal ? (project.current_funding / project.funding_goal) * 100 : 0,
    [project?.current_funding, project?.funding_goal]
  )

  const handleShare = () => {
    setIsShareModalOpen(true)
  }

  const handleEditAndResubmit = () => {
    // Navigate to edit page for rejected projects
    navigate(`/create?edit=${project.id}`)
  }

  // Check if current user is the project owner
  const isOwner = user && project && user.id === project.owner_id

  if (loading) {
    return (
      <motion.div 
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        className="min-h-screen bg-gradient-to-br from-neutral-50 via-white to-neutral-100/30 p-3 sm:p-4 lg:p-6"
      >
        <div className="max-w-6xl mx-auto">
          <div className="flex items-center gap-3 mb-6 p-4 rounded-2xl bg-white/70 backdrop-blur-md border border-white/20 shadow-lg">
            <Skeleton className="h-10 w-10 rounded-xl" />
            <Skeleton className="h-5 w-28" />
          </div>
          
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 sm:gap-6">
            <div className="lg:col-span-2 space-y-4 sm:space-y-6">
              <Card className="overflow-hidden border-0 shadow-[0_8px_32px_0_rgba(31,38,135,0.15)] bg-white/80 backdrop-blur-md rounded-2xl">
                <Skeleton className="h-64 sm:h-80 w-full" />
                <CardContent className="p-4 sm:p-6 lg:p-8">
                  <Skeleton className="h-6 sm:h-8 w-3/4 mb-4 sm:mb-6" />
                  <Skeleton className="h-3 sm:h-4 w-full mb-2 sm:mb-3" />
                  <Skeleton className="h-3 sm:h-4 w-2/3 mb-4 sm:mb-6" />
                  <div className="flex flex-wrap gap-2 sm:gap-3 mb-4 sm:mb-6">
                    <Skeleton className="h-6 sm:h-7 w-20 sm:w-24 rounded-full" />
                    <Skeleton className="h-6 sm:h-7 w-24 sm:w-28 rounded-full" />
                  </div>
                </CardContent>
              </Card>
            </div>
            <div className="space-y-4 sm:space-y-6">
              <Card className="border-0 shadow-[0_8px_32px_0_rgba(31,38,135,0.15)] bg-white/80 backdrop-blur-md rounded-2xl">
                <CardContent className="p-4 sm:p-6 lg:p-8">
                  <Skeleton className="h-10 sm:h-12 w-full mb-3 sm:mb-4" />
                  <Skeleton className="h-3 sm:h-4 w-3/4 mb-4 sm:mb-6" />
                  <Skeleton className="h-2 sm:h-3 w-full mb-3 sm:mb-4 rounded-full" />
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </motion.div>
    )
  }

  if (error) {
    return (
      <motion.div 
        initial={{ opacity: 0, scale: 0.9 }}
        animate={{ opacity: 1, scale: 1 }}
        className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100/50 flex items-center justify-center p-4"
      >
        <Card className="max-w-md w-full border-0 shadow-2xl bg-white/90 backdrop-blur-sm">
          <CardContent className="p-8 text-center">
            <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-6">
              <ArrowLeft className="w-8 h-8 text-red-600" />
            </div>
            <h2 className="text-2xl font-bold text-slate-900 mb-4">Project Not Found</h2>
            <p className="text-slate-600 mb-8">{error}</p>
            <Button 
              onClick={() => navigate('/dashboard')} 
              className="bg-primary-teal hover:bg-primary-teal/90 shadow-lg hover:shadow-xl transition-all duration-200"
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back to Dashboard
            </Button>
          </CardContent>
        </Card>
      </motion.div>
    )
  }

  if (!project) return null

  return (
    <div className="min-h-screen bg-gradient-to-br from-neutral-50 via-white to-neutral-100/30 pb-20 sm:pb-8">
      <motion.div 
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6, ease: [0.4, 0, 0.2, 1] }}
        className="max-w-6xl mx-auto p-3 sm:p-4 lg:p-6"
      >
        {/* Enhanced Header */}
        <motion.div 
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 sm:gap-4 mb-6 sm:mb-8 p-4 sm:p-6 rounded-2xl bg-white/70 backdrop-blur-md border border-white/20 shadow-[0_8px_32px_0_rgba(31,38,135,0.15)]"
        >
          <Button
            onClick={() => navigate('/dashboard')}
            variant="outline"
            size="sm"
            className="text-neutral-600 hover:text-neutral-900 border-neutral-200 hover:border-neutral-300 hover:bg-white/80 transition-all duration-300 hover:scale-105 min-h-[44px]"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Dashboard
          </Button>
          
          <div className="flex items-center gap-2 sm:gap-3 w-full sm:w-auto">
            <Button
              onClick={handleShare}
              variant="outline"
              size="sm"
              disabled={project.status === PROJECT_STATUS.DRAFT || project.status === PROJECT_STATUS.SUBMITTED}
              className="flex-1 sm:flex-none text-neutral-600 hover:text-neutral-900 border-neutral-200 hover:border-neutral-300 hover:bg-white/80 transition-all duration-300 hover:scale-105 min-h-[44px] disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:scale-100"
            >
              <Share2 className="w-4 h-4 mr-2" />
              {project.status === PROJECT_STATUS.DRAFT || project.status === PROJECT_STATUS.SUBMITTED ? 'Share Disabled' : 'Share'}
            </Button>
            {(project.status === PROJECT_STATUS.DRAFT || project.status === PROJECT_STATUS.REJECTED) && (
              <Button
                onClick={() => navigate(`/create?edit=${project.id}`)}
                size="sm"
                className="flex-1 sm:flex-none bg-primary-teal hover:bg-primary-teal/90 shadow-md hover:shadow-lg transition-all duration-300 hover:scale-105 min-h-[44px]"
              >
                Edit Project
              </Button>
            )}
          </div>
        </motion.div>

        {/* Rejection Notice - Only shown to project owners for rejected projects */}
        {isOwner && project.status === PROJECT_STATUS.REJECTED && project.rejection_reason && (
          <motion.div 
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.25 }}
            className="mb-6"
          >
            <Card className="border-2 border-amber-200 bg-amber-50/80 backdrop-blur-md rounded-2xl shadow-[0_8px_32px_0_rgba(251,191,36,0.15)]">
              <CardContent className="p-4 sm:p-6">
                <div className="flex items-start gap-3 sm:gap-4">
                  <div className="p-2 sm:p-3 bg-amber-100 rounded-xl flex-shrink-0">
                    <AlertCircle className="w-5 h-5 sm:w-6 sm:h-6 text-amber-600" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <h3 className="text-base sm:text-lg font-semibold text-amber-900 mb-2 sm:mb-3">
                      Your Project Needs Some Improvements
                    </h3>
                    <div className="bg-white/60 rounded-lg p-3 sm:p-4 mb-3 sm:mb-4">
                      <p className="text-amber-800 leading-relaxed text-sm sm:text-base">
                        {project.rejection_reason}
                      </p>
                    </div>
                    {project.rejected_at && (
                      <p className="text-xs sm:text-sm text-amber-700 mb-4">
                        Reviewed on {new Date(project.rejected_at).toLocaleDateString()}
                      </p>
                    )}
                    <div className="flex flex-col sm:flex-row gap-2 sm:gap-3">
                      <Button 
                        onClick={handleEditAndResubmit}
                        className="bg-amber-600 hover:bg-amber-700 text-white shadow-md hover:shadow-lg transition-all duration-300 text-sm sm:text-base"
                      >
                        <Edit className="w-4 h-4 mr-2" />
                        Edit & Resubmit Project
                      </Button>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          </motion.div>
        )}

        {/* Status Banner - Show current status prominently for owners */}
        {isOwner && (
          <motion.div 
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
            className="mb-6"
          >
            <Card className={`border-0 shadow-[0_8px_32px_0_rgba(31,38,135,0.15)] rounded-2xl ${
              project.status === PROJECT_STATUS.SUBMITTED ? 'bg-blue-50/80' :
              project.status === PROJECT_STATUS.ACTIVE ? 'bg-green-50/80' :
              project.status === PROJECT_STATUS.COMPLETED ? 'bg-purple-50/80' :
              project.status === PROJECT_STATUS.DRAFT ? 'bg-gray-50/80' :
              'bg-white/80'
            } backdrop-blur-md`}>
              <CardContent className="p-4 sm:p-6 text-center">
                <div className="flex items-center justify-center gap-2 sm:gap-3">
                  <Badge className={`${statusConfig.color} border font-medium px-3 py-1.5 rounded-full text-sm sm:text-base`}>
                    {statusConfig.text}
                  </Badge>
                </div>
                <p className={`mt-2 text-sm sm:text-base font-medium ${
                  project.status === PROJECT_STATUS.SUBMITTED ? 'text-blue-700' :
                  project.status === PROJECT_STATUS.ACTIVE ? 'text-green-700' :
                  project.status === PROJECT_STATUS.COMPLETED ? 'text-purple-700' :
                  project.status === PROJECT_STATUS.DRAFT ? 'text-gray-700' :
                  'text-neutral-700'
                }`}>
                  {project.status === PROJECT_STATUS.SUBMITTED && "Your project is under review by our team"}
                  {project.status === PROJECT_STATUS.ACTIVE && "Your project is live and accepting donations"}
                  {project.status === PROJECT_STATUS.COMPLETED && "Your campaign has ended"}
                  {project.status === PROJECT_STATUS.DRAFT && "This is a draft - complete and submit for review"}
                </p>
              </CardContent>
            </Card>
          </motion.div>
        )}

        {/* Main Content Grid */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 sm:gap-6">
          {/* Project Details */}
          <motion.div 
            initial={{ opacity: 0, x: -30 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: 0.3 }}
            className="lg:col-span-2 space-y-4 sm:space-y-6"
          >
            {/* Hero Card */}
            <Card className="overflow-hidden border-0 shadow-[0_8px_32px_0_rgba(31,38,135,0.15)] bg-white/80 backdrop-blur-md rounded-2xl hover:shadow-[0_12px_40px_0_rgba(31,38,135,0.2)] transition-all duration-300 hover:scale-[1.01]">
              {project.cover_image && (
                <div className="h-48 sm:h-64 lg:h-72 overflow-hidden relative group">
                  <img
                    src={project.cover_image}
                    alt={project.title}
                    className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/30 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
                </div>
              )}
              <CardContent className="p-4 sm:p-6 lg:p-8">
                <div className="flex flex-col gap-4 mb-4 sm:mb-6">
                  <div className="flex-1 min-w-0">
                    <h1 className="text-xl sm:text-2xl md:text-3xl lg:text-4xl font-bold text-neutral-900 mb-3 sm:mb-4 leading-tight">
                      {project.title}
                    </h1>
                    <div className="flex flex-wrap items-center gap-2 sm:gap-3">
                      <Badge className={`${statusConfig.color} border font-medium px-3 py-1.5 rounded-full text-xs sm:text-sm transition-all duration-300 hover:scale-105`}>
                        {statusConfig.text}
                      </Badge>
                      {project.category && (
                        <Badge variant="outline" className="capitalize border-neutral-200 text-neutral-700 px-3 py-1.5 rounded-full text-xs sm:text-sm transition-all duration-300 hover:scale-105 hover:border-primary-teal/30 hover:text-primary-teal">
                          {project.category}
                        </Badge>
                      )}
                    </div>
                  </div>
                </div>

                {project.video_url && (
                  <div className="mb-6 rounded-2xl overflow-hidden bg-black shadow-lg border border-neutral-100">
                    <video
                      src={project.video_url}
                      poster={project.cover_image}
                      controls
                      className="w-full aspect-video object-contain"
                    />
                  </div>
                )}

                {project.story && (
                  <div className="prose prose-sm sm:prose-base lg:prose-lg max-w-none">
                    <h3 className="text-lg sm:text-xl font-semibold mb-3 sm:mb-4 text-neutral-900">Project Story</h3>
                    <div className="text-neutral-700 leading-relaxed whitespace-pre-wrap text-sm sm:text-base lg:text-lg">
                      {project.story}
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Enhanced Funding Breakdown */}
            {project.funding_use?.length > 0 && (
              <Card className="border-0 shadow-[0_8px_32px_0_rgba(31,38,135,0.15)] bg-white/80 backdrop-blur-md rounded-2xl hover:shadow-[0_12px_40px_0_rgba(31,38,135,0.2)] transition-all duration-300">
                <CardHeader className="pb-3 sm:pb-4 p-4 sm:p-6">
                  <CardTitle className="flex items-center gap-2 sm:gap-3 text-lg sm:text-xl">
                    <div className="p-2 bg-primary-teal/10 rounded-xl">
                      <Target className="w-5 h-5 sm:w-6 sm:h-6 text-primary-teal" />
                    </div>
                    Funding Breakdown
                  </CardTitle>
                </CardHeader>
                <CardContent className="pt-0 p-4 sm:p-6">
                  <div className="grid gap-3 sm:gap-4">
                    {project.funding_use.map((item, index) => (
                      <motion.div 
                        key={index}
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: 0.1 * index }}
                        className="flex flex-col sm:flex-row sm:items-center justify-between p-3 sm:p-4 bg-gradient-to-r from-neutral-50 to-neutral-100/50 rounded-xl border border-neutral-100 hover:shadow-md hover:scale-[1.02] transition-all duration-300"
                      >
                        <div className="flex-1 min-w-0 mb-2 sm:mb-0">
                          <h4 className="font-semibold text-neutral-900 mb-1 text-sm sm:text-base">{item.category}</h4>
                          {item.description && (
                            <p className="text-xs sm:text-sm text-neutral-600 line-clamp-2">{item.description}</p>
                          )}
                        </div>
                        <div className="text-left sm:text-right sm:ml-4 flex-shrink-0">
                          <p className="font-bold text-neutral-900 text-base sm:text-lg">{getCurrencySymbol(project.currency)} {item.amount}</p>
                          <p className="text-xs sm:text-sm text-primary-teal font-medium">{item.percentage}%</p>
                        </div>
                      </motion.div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Gallery Images */}
            {project.gallery_images?.length > 0 && (
              <Card className="border-0 shadow-[0_8px_32px_0_rgba(31,38,135,0.15)] bg-white/80 backdrop-blur-md rounded-2xl hover:shadow-[0_12px_40px_0_rgba(31,38,135,0.2)] transition-all duration-300">
                <CardHeader className="pb-3 sm:pb-4 p-4 sm:p-6">
                  <CardTitle className="flex items-center gap-2 sm:gap-3 text-lg sm:text-xl">
                    <div className="p-2 bg-primary-teal/10 rounded-xl">
                      <Eye className="w-5 h-5 sm:w-6 sm:h-6 text-primary-teal" />
                    </div>
                    Project Gallery
                  </CardTitle>
                </CardHeader>
                <CardContent className="pt-0 p-4 sm:p-6">
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-4">
                    {project.gallery_images.map((image, index) => (
                      <motion.div 
                        key={index}
                        initial={{ opacity: 0, scale: 0.9 }}
                        animate={{ opacity: 1, scale: 1 }}
                        transition={{ delay: 0.1 * index }}
                        className="aspect-video overflow-hidden rounded-xl border border-neutral-100 hover:shadow-md hover:scale-[1.02] transition-all duration-300 group"
                      >
                        <img
                          src={image}
                          alt={`Project gallery image ${index + 1}`}
                          className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                        />
                      </motion.div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Enhanced Team Members */}
            {project.team_members?.length > 0 && (
              <Card className="border-0 shadow-[0_8px_32px_0_rgba(31,38,135,0.15)] bg-white/80 backdrop-blur-md rounded-2xl hover:shadow-[0_12px_40px_0_rgba(31,38,135,0.2)] transition-all duration-300">
                <CardHeader className="pb-3 sm:pb-4 p-4 sm:p-6">
                  <CardTitle className="flex items-center gap-2 sm:gap-3 text-lg sm:text-xl">
                    <div className="p-2 bg-primary-teal/10 rounded-xl">
                      <User className="w-5 h-5 sm:w-6 sm:h-6 text-primary-teal" />
                    </div>
                    Team Members
                  </CardTitle>
                </CardHeader>
                <CardContent className="pt-0 p-4 sm:p-6">
                  <div className="grid grid-cols-1 gap-3 sm:gap-4">
                    {project.team_members.map((member, index) => (
                      <motion.div 
                        key={index}
                        initial={{ opacity: 0, scale: 0.9 }}
                        animate={{ opacity: 1, scale: 1 }}
                        transition={{ delay: 0.1 * index }}
                        className="flex items-center gap-3 sm:gap-4 p-3 sm:p-4 bg-gradient-to-r from-neutral-50 to-neutral-100/50 rounded-xl border border-neutral-100 hover:shadow-md hover:scale-[1.02] transition-all duration-300"
                      >
                        <div className="w-10 h-10 sm:w-12 sm:h-12 bg-primary-teal/10 rounded-full flex items-center justify-center flex-shrink-0 border border-primary-teal/20">
                          <User className="w-5 h-5 sm:w-6 sm:h-6 text-primary-teal" />
                        </div>
                        <div className="min-w-0 flex-1">
                          <h4 className="font-semibold text-neutral-900 truncate text-sm sm:text-base">{member.name}</h4>
                          <p className="text-xs sm:text-sm text-neutral-600 truncate">{member.role}</p>
                        </div>
                      </motion.div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Risks & Challenges */}
            {project.risks_challenges && (
              <Card className="border-0 shadow-[0_8px_32px_0_rgba(31,38,135,0.15)] bg-white/80 backdrop-blur-md rounded-2xl hover:shadow-[0_12px_40px_0_rgba(31,38,135,0.2)] transition-all duration-300">
                <CardHeader className="pb-3 sm:pb-4 p-4 sm:p-6">
                  <CardTitle className="flex items-center gap-2 sm:gap-3 text-lg sm:text-xl">
                    <div className="p-2 bg-amber-100 rounded-xl">
                      <AlertCircle className="w-5 h-5 sm:w-6 sm:h-6 text-amber-600" />
                    </div>
                    Risks & Challenges
                  </CardTitle>
                </CardHeader>
                <CardContent className="pt-0 p-4 sm:p-6">
                  <div className="prose prose-sm sm:prose-base max-w-none">
                    <div className="text-neutral-700 leading-relaxed whitespace-pre-wrap text-sm sm:text-base">
                      {project.risks_challenges}
                    </div>
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Milestones */}
            {project.milestones?.length > 0 && (
              <Card className="border-0 shadow-[0_8px_32px_0_rgba(31,38,135,0.15)] bg-white/80 backdrop-blur-md rounded-2xl hover:shadow-[0_12px_40px_0_rgba(31,38,135,0.2)] transition-all duration-300">
                <CardHeader className="pb-3 sm:pb-4 p-4 sm:p-6">
                  <CardTitle className="flex items-center gap-2 sm:gap-3 text-lg sm:text-xl">
                    <div className="p-2 bg-primary-teal/10 rounded-xl">
                      <Calendar className="w-5 h-5 sm:w-6 sm:h-6 text-primary-teal" />
                    </div>
                    Project Milestones
                  </CardTitle>
                </CardHeader>
                <CardContent className="pt-0 p-4 sm:p-6">
                  <div className="space-y-4">
                    {project.milestones.map((milestone, index) => (
                      <motion.div 
                        key={index}
                        initial={{ opacity: 0, x: -20 }}
                        animate={{ opacity: 1, x: 0 }}
                        transition={{ delay: 0.1 * index }}
                        className="flex gap-4 p-4 bg-gradient-to-r from-neutral-50 to-neutral-100/50 rounded-xl border border-neutral-100 hover:shadow-md hover:scale-[1.01] transition-all duration-300"
                      >
                        <div className="flex-shrink-0 w-8 h-8 bg-primary-teal/10 rounded-full flex items-center justify-center border border-primary-teal/20">
                          <span className="text-xs font-semibold text-primary-teal">{index + 1}</span>
                        </div>
                        <div className="flex-1 min-w-0">
                          <h4 className="font-semibold text-neutral-900 mb-1 text-sm sm:text-base">{milestone.title}</h4>
                          {milestone.date && (
                            <p className="text-xs sm:text-sm text-primary-teal font-medium mb-2">
                              Target: {new Date(milestone.date).toLocaleDateString()}
                            </p>
                          )}
                          {milestone.description && (
                            <p className="text-xs sm:text-sm text-neutral-600 leading-relaxed">
                              {milestone.description}
                            </p>
                          )}
                        </div>
                      </motion.div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}
          </motion.div>

          {/* Enhanced Sidebar */}
          <motion.div 
            initial={{ opacity: 0, x: 30 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: 0.4 }}
            className="space-y-4 sm:space-y-6"
          >
            {/* Enhanced Funding Progress */}
            <Card className="border-0 shadow-[0_8px_32px_0_rgba(31,38,135,0.15)] bg-white/80 backdrop-blur-md rounded-2xl lg:sticky lg:top-6 hover:shadow-[0_12px_40px_0_rgba(31,38,135,0.2)] transition-all duration-300">
              <CardContent className="p-4 sm:p-6 lg:p-8">
                <div className="text-center mb-4 sm:mb-6">
                  <div className="text-2xl sm:text-3xl lg:text-4xl font-bold text-neutral-900 mb-2">
                    {getCurrencySymbol(project.currency)} {new Intl.NumberFormat('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 2 }).format(project.current_funding || 0)}
                  </div>
                  <p className="text-sm sm:text-base text-neutral-600">
                    raised of {getCurrencySymbol(project.currency)} {new Intl.NumberFormat('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 2 }).format(project.funding_goal || 0)} goal
                  </p>
                </div>
                
                {project.funding_goal && (
                  <div className="mb-4 sm:mb-6">
                    <div className="w-full bg-neutral-200 rounded-full h-2.5 sm:h-3 overflow-hidden shadow-inner">
                      <motion.div
                        initial={{ width: 0 }}
                        animate={{ width: `${Math.min(fundingPercentage, 100)}%` }}
                        transition={{ duration: 1.5, ease: "easeOut" }}
                        className="bg-gradient-to-r from-primary-teal via-primary-teal to-primary-teal/80 h-full rounded-full shadow-sm"
                      />
                    </div>
                    <p className="text-xs sm:text-sm text-neutral-600 mt-2 sm:mt-3 text-center font-medium">
                      {new Intl.NumberFormat('en-US', { maximumFractionDigits: 1 }).format(fundingPercentage)}% funded
                    </p>
                  </div>
                )}

                <div className="space-y-3 sm:space-y-4">
                  {project.view_count && (
                    <div className="flex items-center justify-between p-3 bg-neutral-50/80 rounded-xl hover:bg-neutral-100/80 transition-all duration-300 hover:scale-[1.02]">
                      <span className="flex items-center gap-2 text-neutral-600 text-sm sm:text-base">
                        <Eye className="w-4 h-4" />
                        Views
                      </span>
                      <span className="font-semibold text-neutral-900 text-sm sm:text-base">{project.view_count.toLocaleString()}</span>
                    </div>
                  )}
                  
                  {project.campaign_end_date && (
                    <div className="flex items-center justify-between p-3 bg-neutral-50/80 rounded-xl hover:bg-neutral-100/80 transition-all duration-300 hover:scale-[1.02]">
                      <span className="flex items-center gap-2 text-neutral-600 text-sm sm:text-base">
                        <Calendar className="w-4 h-4" />
                        Campaign Ends
                      </span>
                      <span className="font-semibold text-neutral-900 text-xs sm:text-sm">
                        {new Date(project.campaign_end_date).toLocaleDateString()}
                      </span>
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>

            {/* Enhanced Project Owner */}
            {project.owner && (
              <Card className="border-0 shadow-[0_8px_32px_0_rgba(31,38,135,0.15)] bg-white/80 backdrop-blur-md rounded-2xl hover:shadow-[0_12px_40px_0_rgba(31,38,135,0.2)] transition-all duration-300">
                <CardHeader className="pb-3 sm:pb-4 p-4 sm:p-6">
                  <CardTitle className="text-base sm:text-lg">Project Owner</CardTitle>
                </CardHeader>
                <CardContent className="pt-0 p-4 sm:p-6">
                  <div className="flex items-center gap-3 sm:gap-4">
                    {project.owner.profile_image ? (
                      <img
                        src={project.owner.profile_image}
                        alt={project.owner.full_name || project.owner.pi_username}
                        className="w-12 h-12 sm:w-16 sm:h-16 rounded-full object-cover border-2 border-primary-teal/20 transition-transform duration-300 hover:scale-105"
                      />
                    ) : (
                      <div className="w-12 h-12 sm:w-16 sm:h-16 bg-primary-teal/10 rounded-full flex items-center justify-center border-2 border-primary-teal/20 transition-transform duration-300 hover:scale-105">
                        <User className="w-6 h-6 sm:w-8 sm:h-8 text-primary-teal" />
                      </div>
                    )}
                    <div className="min-w-0 flex-1">
                      <h4 className="font-semibold text-neutral-900 mb-1 truncate text-sm sm:text-base">
                        {project.owner.full_name || project.owner.pi_username}
                      </h4>
                      {project.owner.badge_level && (
                        <Badge variant="outline" className="text-xs border-primary-teal/30 text-primary-teal rounded-full px-2 py-1 transition-all duration-300 hover:scale-105">
                          {project.owner.badge_level} Member
                        </Badge>
                      )}
                    </div>
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Project Details */}
            <Card className="border-0 shadow-[0_8px_32px_0_rgba(31,38,135,0.15)] bg-white/80 backdrop-blur-md rounded-2xl hover:shadow-[0_12px_40px_0_rgba(31,38,135,0.2)] transition-all duration-300">
              <CardHeader className="pb-3 sm:pb-4 p-4 sm:p-6">
                <CardTitle className="text-base sm:text-lg">Project Details</CardTitle>
              </CardHeader>
              <CardContent className="pt-0 p-4 sm:p-6 space-y-3 sm:space-y-4">
                {project.funding_for_display && (
                  <motion.div 
                    whileHover={{ scale: 1.02 }}
                    className="flex items-center gap-3 p-3 bg-neutral-50/80 rounded-xl transition-all duration-300 min-h-[48px]"
                  >
                    <Target className="w-4 h-4 sm:w-5 sm:h-5 text-neutral-500 flex-shrink-0" />
                    <span className="text-neutral-700 font-medium text-sm sm:text-base">
                      Funding for: {project.funding_for_display}
                    </span>
                  </motion.div>
                )}

                {project.campaign_duration && (
                  <motion.div 
                    whileHover={{ scale: 1.02 }}
                    className="flex items-center gap-3 p-3 bg-neutral-50/80 rounded-xl transition-all duration-300 min-h-[48px]"
                  >
                    <Calendar className="w-4 h-4 sm:w-5 sm:h-5 text-neutral-500 flex-shrink-0" />
                    <span className="text-neutral-700 font-medium text-sm sm:text-base">
                      Duration: {project.campaign_duration.replace('_', ' ').replace(/(\d+)/, '$1 ')}
                    </span>
                  </motion.div>
                )}

                {project.submitted_at && (
                  <motion.div 
                    whileHover={{ scale: 1.02 }}
                    className="flex items-center gap-3 p-3 bg-neutral-50/80 rounded-xl transition-all duration-300 min-h-[48px]"
                  >
                    <Calendar className="w-4 h-4 sm:w-5 sm:h-5 text-neutral-500 flex-shrink-0" />
                    <span className="text-neutral-700 font-medium text-sm sm:text-base">
                      Submitted: {new Date(project.submitted_at).toLocaleDateString()}
                    </span>
                  </motion.div>
                )}

                {project.donor_count !== undefined && (
                  <motion.div 
                    whileHover={{ scale: 1.02 }}
                    className="flex items-center gap-3 p-3 bg-neutral-50/80 rounded-xl transition-all duration-300 min-h-[48px]"
                  >
                    <User className="w-4 h-4 sm:w-5 sm:h-5 text-neutral-500 flex-shrink-0" />
                    <span className="text-neutral-700 font-medium text-sm sm:text-base">
                      {project.donor_count} {project.donor_count === 1 ? 'Donor' : 'Donors'}
                    </span>
                  </motion.div>
                )}
              </CardContent>
            </Card>

            {/* Enhanced Contact Information */}
            <Card className="border-0 shadow-[0_8px_32px_0_rgba(31,38,135,0.15)] bg-white/80 backdrop-blur-md rounded-2xl hover:shadow-[0_12px_40px_0_rgba(31,38,135,0.2)] transition-all duration-300">
              <CardHeader className="pb-3 sm:pb-4 p-4 sm:p-6">
                <CardTitle className="text-base sm:text-lg">Contact Information</CardTitle>
              </CardHeader>
              <CardContent className="pt-0 p-4 sm:p-6 space-y-3 sm:space-y-4">
                {project.contact_email && (
                  <motion.div 
                    whileHover={{ scale: 1.02 }}
                    className="flex items-center gap-3 p-3 bg-neutral-50/80 rounded-xl hover:bg-neutral-100/80 transition-all duration-300 min-h-[48px]"
                  >
                    <Mail className="w-4 h-4 sm:w-5 sm:h-5 text-neutral-500 flex-shrink-0" />
                    <a
                      href={`mailto:${project.contact_email}`}
                      className="text-primary-teal hover:underline font-medium truncate text-sm sm:text-base transition-colors duration-300"
                    >
                      {project.contact_email}
                    </a>
                  </motion.div>
                )}
                
                {project.country && (
                  <motion.div 
                    whileHover={{ scale: 1.02 }}
                    className="flex items-center gap-3 p-3 bg-neutral-50/80 rounded-xl transition-all duration-300 min-h-[48px]"
                  >
                    <MapPin className="w-4 h-4 sm:w-5 sm:h-5 text-neutral-500 flex-shrink-0" />
                    <span className="text-neutral-700 font-medium text-sm sm:text-base">
                      {project.country}{project.region && `, ${project.region}`}
                    </span>
                  </motion.div>
                )}
                
                {project.app_url && (
                  <motion.div 
                    whileHover={{ scale: 1.02 }}
                    className="flex items-center gap-3 p-3 bg-neutral-50/80 rounded-xl hover:bg-neutral-100/80 transition-all duration-300 min-h-[48px]"
                  >
                    <Globe className="w-4 h-4 sm:w-5 sm:h-5 text-neutral-500 flex-shrink-0" />
                    <a
                      href={project.app_url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-primary-teal hover:underline font-medium truncate text-sm sm:text-base transition-colors duration-300"
                    >
                      Visit App
                    </a>
                  </motion.div>
                )}
              </CardContent>
            </Card>
          </motion.div>
        </div>
      </motion.div>

      {/* Share Modal */}
      <ShareModal
        project={project}
        isOpen={isShareModalOpen}
        onClose={() => setIsShareModalOpen(false)}
      />
    </div>
  )
}

export default ProjectDetailPage
